import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { Collapse } from '@mui/material';
import { FlexColumn } from '../../Flex';
import { pluralizeString } from '../../../utils/common';
import { CollapseArrowIcon, formatToolName, ToolText } from './toolDisplay';
import ToolCallChip from './ToolCallChip';
import { latestInvocationsById, ToolInvocation } from './messageParts';

const AUTO_COLLAPSE_DELAY_MS = 800;

const HeaderRow = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font: inherit;
  color: inherit;

  &:hover ${CollapseArrowIcon} {
    opacity: 1;
  }
`;

const ToolList = styled(FlexColumn)`
  gap: 2px;
  margin-left: 9px;
  padding-left: 16px;
  border-left: 1px solid ${({ theme }) => theme.palette.common.gray5};
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
`;

type Props = {
  invocations: ToolInvocation[];
  persistedExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  isCollapsed?: boolean;
};

const ToolCallGroup = ({
  invocations,
  persistedExpanded,
  onExpandedChange,
  isCollapsed = false,
}: Props) => {
  const latestByToolCallId = latestInvocationsById(invocations);
  const allDone = latestByToolCallId.every((inv) => inv.state === 'result');
  const [done, setDone] = useState(allDone);
  const [expanded, setExpanded] = useState(persistedExpanded ?? !isCollapsed);
  const timerStartedRef = useRef(false);
  const userToggledRef = useRef(persistedExpanded !== undefined);

  const errorCount = latestByToolCallId.filter(
    (inv) => inv.result?.isError,
  ).length;
  const count = latestByToolCallId.length;

  useEffect(() => {
    if (!allDone) {
      timerStartedRef.current = false;
      if (done) setDone(false);
      return undefined;
    }
    if (done || timerStartedRef.current) return undefined;
    timerStartedRef.current = true;
    const timer = setTimeout(() => setDone(true), AUTO_COLLAPSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [allDone, done]);

  useEffect(() => {
    if (userToggledRef.current) return;
    setExpanded(!isCollapsed);
  }, [isCollapsed]);

  const single = count === 1 ? latestByToolCallId[0] : undefined;
  const singleGoal = (single?.args?.goal as string | undefined)?.trim();
  const singleLabel = single
    ? singleGoal || formatToolName(single.toolName)
    : undefined;

  const baseLabel = done
    ? pluralizeString(count, 'tool', 'tools')
    : singleLabel ?? `Running ${pluralizeString(count, 'tool', 'tools')}...`;

  const errorLabel =
    done && errorCount > 0
      ? pluralizeString(errorCount, 'error', 'errors')
      : null;

  const HeaderIcon = done
    ? errorCount > 0
      ? ErrorRoundedIcon
      : CheckCircleRoundedIcon
    : SearchRoundedIcon;

  return (
    <FlexColumn>
      <HeaderRow
        aria-expanded={expanded}
        onClick={() =>
          setExpanded((prev) => {
            const next = !prev;
            userToggledRef.current = true;
            onExpandedChange?.(next);
            return next;
          })
        }
      >
        <HeaderIcon sx={{ fontSize: '18px', color: 'common.magenta' }} />
        <ToolText $running={!done}>
          {baseLabel}
          {errorLabel && (
            <>
              {' · '}
              <ErrorText>{errorLabel}</ErrorText>
            </>
          )}
        </ToolText>
        <CollapseArrowIcon $expanded={expanded} />
      </HeaderRow>
      <Collapse in={expanded} unmountOnExit>
        <ToolList>
          {latestByToolCallId.map((inv) => (
            <ToolCallChip key={inv.toolCallId} invocation={inv} />
          ))}
        </ToolList>
      </Collapse>
    </FlexColumn>
  );
};

export default ToolCallGroup;
